import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import setAuthToken from '../utils/setAuthToken';
import { useTranslation} from 'react-i18next';

const RegisterPage = () => 
  {
    
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'user' });
  // Destructure for easier usage
  const [agreed, setAgreed] = useState(false);

  const { name, email, password, role } = formData;
  
  const onChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      alert(t('profile.alerts.agree-terms-conditions'));
      return;
    }
  
    try {
      const res = await api.post('/auth/register', { name, email, password, role });
      
      // IMPORTANT: Remove the localStorage.token logic here
      // Instead, notify the user and redirect to a "Verify Notice" page
      alert(t('general.alerts.registration-success')  || res.data.msg); 
      navigate('/login'); // Or a custom /verify-notice page
    } catch (err) {
      alert(err.response?.data?.msg || t('profile.alerts.registration-failed'));
    }
  };

  return (
    <div className="min-h-full flex flex-col justify-center px-6 py-12 lg:px-8 bg-white">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm text-center">
        <h2 className="text-2xl font-bold leading-9 tracking-tight text-gray-900"> {t('profile.welcome.register-title')} </h2>
        <p className="mt-2 text-sm text-gray-500"> {t('profile.welcome.register-subtitle')} </p>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-900">{t('profile.full-name')}</label>
            <input required className="w-full border rounded-md p-2 mt-1" 
                   value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900">{t('profile.email')}</label>
            <input type="email" required className="w-full border rounded-md p-2 mt-1" 
                   value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900">{t('profile.password')}</label>
            <input type="password" required className="w-full border rounded-md p-2 mt-1" 
                   value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
          </div>

          {/* --- ROLE SELECTION --- */}
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">{t('profile.welcome.here-to')}</label>
            <div className="grid grid-cols-2 gap-4">
              {/* Option 1: End User */}
              <label 
                className={`cursor-pointer border rounded-lg p-3 text-center transition-all ${
                  role === 'user' 
                    ? 'bg-blue-50 border-blue-500 text-blue-700 ring-2 ring-blue-200' 
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                <input 
                  type="radio" 
                  name="role" 
                  value="user" 
                  className="hidden"
                  checked={role === 'user'}
                  onChange={e => setFormData({ ...formData, role: e.target.value })}
                />
                <div className="font-bold">{t('profile.welcome.find-services')}</div>
                <div className="text-xs mt-1">{t('profile.welcome.find-services-desc')}</div>
              </label>

              {/* Option 2: Service Provider */}
              <label 
                className={`cursor-pointer border rounded-lg p-3 text-center transition-all ${
                  role === 'provider' 
                    ? 'bg-blue-50 border-blue-500 text-blue-700 ring-2 ring-blue-200' 
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                <input 
                  type="radio" 
                  name="role" 
                  value="provider" 
                  className="hidden"
                  checked={role === 'provider'}
                  onChange={e => setFormData({ ...formData, role: e.target.value })}
                />
                <div className="font-bold">{t('profile.welcome.offer-services')}</div>
                <div className="text-xs mt-1">{t('profile.welcome.offer-services-desc')}</div>
              </label>
            </div>
          </div>          
          {/* --------------------------- */}

          {/* Accept terms and conditions Section */}
          <div className="mb-6 flex items-start">
            <div className="flex items-center h-5">
              <input
                id="terms"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-blue-300"
              />
            </div>
            <label htmlFor="terms" className="ml-2 text-sm font-medium text-gray-900">
             {t('profile.welcome.agree-terms')} <Link to="/terms" target="_blank" className="text-blue-600 hover:underline">{t('terms.terms-and-conditions')}</Link>
            </label>
          </div>
          {/* --------------------------- */}

          <button type="submit" className="flex w-full justify-center rounded-md bg-[#1d5cab] px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-[#2372bd]">
            {t('profile.welcome.create-account-button')}
          </button>
        </form>

        <p className="mt-10 text-center text-sm text-gray-500">
          {t('profile.welcome.already-member')} <Link to="/login" className="font-semibold text-blue-600 hover:text-blue-500">{t('nav.login')}</Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
